import { useEffect, useReducer } from 'react';
import axios from 'axios';

const EVENT = 'adeeb-playlist-change';

let cache = new Set();
let loaded = false;
let inflight = null;

function authHeaders() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('adeeb-token') : null;
  return token ? { Authorization: `Bearer ${token}` } : null;
}

function emit() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(EVENT));
}

export async function loadPlaylist(force = false) {
  if (loaded && !force) return cache;
  if (inflight) return inflight;
  const headers = authHeaders();
  if (!headers) {
    cache = new Set();
    loaded = true;
    emit();
    return cache;
  }
  inflight = axios
    .get('/api/playlist', { headers })
    .then(({ data }) => {
      cache = new Set((Array.isArray(data) ? data : []).map((f) => String(f.bookId)));
      loaded = true;
      emit();
      return cache;
    })
    .catch(() => {
      cache = new Set();
      loaded = true;
      emit();
      return cache;
    })
    .finally(() => { inflight = null; });
  return inflight;
}

export function isInPlaylist(bookId) {
  return cache.has(String(bookId));
}

export function getPlaylist() {
  return new Set(cache);
}

export async function reorderPlaylist(orderedIds) {
  const ids = (Array.isArray(orderedIds) ? orderedIds : []).map(String);
  const headers = authHeaders();
  if (!headers) {
    const err = new Error('Not authenticated');
    err.code = 'NOT_AUTHENTICATED';
    throw err;
  }
  // Optimistic: rebuild the cache Set in the new order. JS Sets preserve
  // insertion order, so consumers iterating playlist.all() get the new layout.
  const prev = cache;
  cache = new Set(ids.filter((id) => prev.has(id)));
  // Append any ids that exist in prev but weren't passed in (defensive).
  for (const id of prev) if (!cache.has(id)) cache.add(id);
  emit();
  try {
    await axios.put('/api/playlist/order', { ids: Array.from(cache) }, { headers });
  } catch (err) {
    cache = prev;
    emit();
    throw err;
  }
}

export async function togglePlaylist(bookId) {
  const id = String(bookId);
  const headers = authHeaders();
  if (!headers) {
    const err = new Error('Not authenticated');
    err.code = 'NOT_AUTHENTICATED';
    throw err;
  }
  const wasIn = cache.has(id);
  if (wasIn) cache.delete(id); else cache.add(id);
  emit();
  try {
    if (wasIn) {
      await axios.delete(`/api/playlist/${encodeURIComponent(id)}`, { headers });
    } else {
      await axios.post('/api/playlist', { bookId: id }, { headers });
    }
    return !wasIn;
  } catch (err) {
    if (wasIn) cache.add(id); else cache.delete(id);
    emit();
    throw err;
  }
}

function clearOnLogout() {
  cache = new Set();
  loaded = false;
  emit();
}

if (typeof window !== 'undefined') {
  window.addEventListener('adeeb-auth-change', () => {
    clearOnLogout();
    if (localStorage.getItem('adeeb-token')) loadPlaylist(true);
  });
  // Profile-switch invalidation — see favorites.js for the same pattern.
  window.addEventListener('adeeb-profile-change', () => {
    clearOnLogout();
    if (localStorage.getItem('adeeb-token')) loadPlaylist(true);
  });
}

export function usePlaylist() {
  const [, force] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    if (!loaded) loadPlaylist();
    const onChange = () => force();
    window.addEventListener(EVENT, onChange);
    return () => window.removeEventListener(EVENT, onChange);
  }, []);
  return {
    has: (id) => cache.has(String(id)),
    toggle: togglePlaylist,
    reorder: reorderPlaylist,
    all: () => new Set(cache),
  };
}
